import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { JsonUtility } from '../../../../Assembler/dist/app/jsonUtility.js';
import { CommonUtil } from '../../../../Assembler/dist/common/commonUtil.js';
import { JsonArray, JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { DynamicConfigMap } from '../../../../Assembler/dist/registry/dynamicConfigMap.js';
import { ResourceStatistics } from '../../../../Assembler/dist/model/index.js';
import type { IConfig } from '../../../../Assembler/dist/config/iConfig.js';
import { ArtifactKind } from '../../../../Assembler/dist/config/iConfig.js';
import { SourceFile, TargetFile } from '../../model/index.js';
import { JsonKeyEntryInfo, TemplateEntryInfo } from '../../model/appSiteMetadata.js';
import type { TemplateInfo } from '../../model/appSiteMetadata.js';
import type { LoaderPreProcessJson } from './loaderPreProcessJson.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';

type PlainJsonObject = { [key: string]: unknown };

function populateTemplateExecutionMetadata(template: TemplateInfo, htmlJson: JsonObject): void {
    template.entries = [];
    template.jsonKeyEntries = [];

    template.hasJsonKeys = false;
    template.hasPlaceholders = false;
    template.hasSlotholders = false;
    template.hasArrayBlocks = false;
    template.hasConditionals = false;

    let nonMetaCount = 0;
    let ordinal = 0;
    for (const key of htmlJson.keys()) {
        if (key === '_meta') {
            continue;
        }

        nonMetaCount++;
        const keyType = key.length > 0 ? key[0] : '\0';
        const value = htmlJson.get(key);
        const name = GenerateHtmlJson.getKeyName(key);
        const entry = new TemplateEntryInfo();
        entry.htmlJsonKey = key;
        entry.entryType = getExecutionEntryType(keyType);
        entry.name = name;
        entry.ordinal = ordinal++;
        entry.isSkipMarked = keyType === 'K' || keyType === 'P' || keyType === 'H' || keyType === 'A';
        entry.hasNestedContent = value.isObject;
        template.entries.push(entry);

        switch (keyType) {
            case 'k':
            case 'K': {
                template.hasJsonKeys = true;
                const jsonKeyEntry = new JsonKeyEntryInfo();
                jsonKeyEntry.htmlJsonKey = key;
                jsonKeyEntry.jsonKey = name;
                jsonKeyEntry.isSkipMarked = keyType === 'K';
                jsonKeyEntry.isInherited = !!template.jsonInheritKeys && template.jsonInheritKeys.has(name);
                template.jsonKeyEntries.push(jsonKeyEntry);
                break;
            }
            case 'p':
            case 'P':
                template.hasPlaceholders = true;
                break;
            case 'h':
            case 'H':
                template.hasSlotholders = true;
                break;
            case 'a':
            case 'A':
                template.hasArrayBlocks = true;
                break;
            case 'c':
                template.hasConditionals = true;
                break;
        }
    }

    template.requiresDataJsonMerge = template.hasJsonKeys;
    template.requiresContextMerge = template.hasJsonKeys || template.hasArrayBlocks || template.hasConditionals;
    template.requiresTemplateTraversal = template.hasPlaceholders || template.hasSlotholders || template.hasArrayBlocks || template.hasConditionals || template.hasJsonKeys;
    template.isStaticHtmlOnly = nonMetaCount > 0 && !template.requiresTemplateTraversal;
    template.canReturnStaticHtml = template.isStaticHtmlOnly;
}

function getExecutionEntryType(keyType: string): string {
    switch (keyType) {
        case 's': return 'Html';
        case 'k': return 'JsonKey';
        case 'K': return 'JsonKeySkip';
        case 'p': return 'Placeholder';
        case 'P': return 'PlaceholderSkip';
        case 'h': return 'Slotholder';
        case 'H': return 'SlotholderSkip';
        case 'a': return 'ArrayBlock';
        case 'A': return 'ArrayBlockSkip';
        case 'x': return 'SlotHtml';
        case 'i': return 'Slot';
        case 'c': return 'Conditional';
        default: return 'Unknown';
    }
}

export const accessorMethods = {
    /**
     * Skip patterns for Dynamic View handlers (comma-delimited or "*").
     * Mirrors C# `LoaderPreProcessJson_Accessors.cs : public string? SkipDynamicView`.
     * Attached to LoaderPreProcessJson.prototype via Object.defineProperties; `this` is bound to the instance at call time.
     */
    get skipDynamicView(): string | null { return (this as unknown as LoaderPreProcessJson)._skipDynamicView; },
    set skipDynamicView(value: string | null) { (this as unknown as LoaderPreProcessJson)._skipDynamicView = value; },

    /**
     * Skip patterns for Dynamic Data handlers (comma-delimited or "*").
     * Mirrors C# `LoaderPreProcessJson_Accessors.cs : public string? SkipDynamicData`.
     */
    get skipDynamicData(): string | null { return (this as unknown as LoaderPreProcessJson)._skipDynamicData; },
    set skipDynamicData(value: string | null) { (this as unknown as LoaderPreProcessJson)._skipDynamicData = value; },

    populateExecutionMetadata(this: LoaderPreProcessJson): void {
        for (const metadata of this._store.getAllMetadata().values()) {
            if (!metadata.rootComponent) {
                continue;
            }

            this.populateExecutionMetadataRecursive(metadata.rootComponent);
        }
    },

    populateExecutionMetadataRecursive(this: LoaderPreProcessJson, component: TemplateInfo): void {
        if (!component) {
            return;
        }

        const htmlJson = this.getHtmlJson(component.templateAppSite, component.templateName);
        if (htmlJson) {
            populateTemplateExecutionMetadata(component, htmlJson);
        }

        if (component.components && Array.isArray(component.components)) {
            for (const child of component.components) {
                this.populateExecutionMetadataRecursive(child);
            }
        }
    },

    buildComponentIndex(this: LoaderPreProcessJson): void {
        this._store.clearComponentIndex();

        for (const metadataKey of this._store.getMetadataKeys()) {
            const metadata = this._store.tryGetMetadata(metadataKey);
            if (!metadata || !metadata.rootComponent) continue;

            const indexPrefix = metadata.appView ? `${metadata.appSiteName}_${metadata.appView}_` : `${metadata.appSiteName}_`;
            this.addComponentToIndex(metadata.rootComponent, indexPrefix);
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`Built component index with ${this._store.getComponentIndexCount()} entries`, 'LoaderPreProcessJson');
        }
    },

    addComponentToIndex(this: LoaderPreProcessJson, component: TemplateInfo, indexPrefix: string): void {
        if (component.placeholderName) {
            const indexKey = indexPrefix + component.placeholderName;
            if (!this._store.hasComponentInfo(indexKey)) {
                this._store.setComponentInfo(indexKey, component);
            }
        }

        if (component.templateName) {
            const indexKey = indexPrefix + component.templateName;
            if (!this._store.hasComponentInfo(indexKey)) {
                this._store.setComponentInfo(indexKey, component);
            }
        }

        if (component.components && Array.isArray(component.components)) {
            for (const child of component.components) {
                this.addComponentToIndex(child, indexPrefix);
            }
        }
    },

    getSearchAppSitesForAppSite(this: LoaderPreProcessJson, appSite: string): string {
        if ((this as unknown as PlainJsonObject)._searchAppSites !== undefined) {
            return (this as unknown as PlainJsonObject)._searchAppSites as string;
        }
        if (this._config && typeof this._config.getSearchAppSitesForAppSite === 'function') {
            return this._config.getSearchAppSitesForAppSite(appSite);
        }
        return '';
    },

    hasTemplate(
        this: LoaderPreProcessJson,
        appSite: string,
        currentTemplateName: string,
        appFileOrPlaceholderOrSlotholderName: string,
        appView: string | null = null
    ): boolean {
        const { htmlJson } = this.getTemplate(appSite, currentTemplateName, appFileOrPlaceholderOrSlotholderName, appView);

        if (!htmlJson || htmlJson.size === 0) {
            return false;
        }

        if (htmlJson.size === 1 && htmlJson.has('001_Html')) {
            const htmlValue = htmlJson.get('001_Html').asString();
            return htmlValue !== this._config!.defaultReplaceHtml;
        }

        return true;
    },

    populateDynamicHandlerFlags(this: LoaderPreProcessJson, component: TemplateInfo, config: LoaderPreProcessJson | IConfig, appSite: string): void {
        if (!component) return;

        const appSitesToCheck = [appSite];
        const configObj = config as unknown as { getSearchAppSitesForAppSite(s: string): string };
        const searchAppSites = configObj.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites) {
            const parts = searchAppSites.split(',');
            for (const part of parts) {
                const trimmed = part.trim();
                if (trimmed && !appSitesToCheck.includes(trimmed)) {
                    appSitesToCheck.push(trimmed);
                }
            }
        }

        component.hasDynamicData = false;
        for (const checkAppSite of appSitesToCheck) {
            const configLike = config as unknown as { getDynamicDataConfig(s: string): unknown };
            const dynamicDataConfig = configLike.getDynamicDataConfig(checkAppSite);
            if (dynamicDataConfig) {
                const dynamicKeys = dynamicDataConfig instanceof DynamicConfigMap
                    ? Array.from(dynamicDataConfig.keys())
                    : Object.keys(dynamicDataConfig as PlainJsonObject);
                if (dynamicKeys.some(key => key === '*')) {
                    component.hasDynamicData = true;
                    break;
                }
                for (const key of dynamicKeys) {
                    if (key.toLowerCase() === component.templateName.toLowerCase() ||
                        (component.placeholderName && key.toLowerCase() === component.placeholderName.toLowerCase())) {
                        component.hasDynamicData = true;
                        break;
                    }
                }
                if (component.hasDynamicData) break;
            }
        }

        component.hasDynamicView = false;
        for (const checkAppSite of appSitesToCheck) {
            const configLike = config as unknown as { getDynamicViewConfig(s: string): unknown };
            const dynamicViewConfig = configLike.getDynamicViewConfig(checkAppSite);
            if (dynamicViewConfig) {
                const dynamicKeys = dynamicViewConfig instanceof DynamicConfigMap
                    ? Array.from(dynamicViewConfig.keys())
                    : Object.keys(dynamicViewConfig as PlainJsonObject);
                if (dynamicKeys.some(key => key === '*')) {
                    component.hasDynamicView = true;
                    break;
                }
                for (const key of dynamicKeys) {
                    if (key.toLowerCase() === component.templateName.toLowerCase() ||
                        (component.placeholderName && key.toLowerCase() === component.placeholderName.toLowerCase())) {
                        component.hasDynamicView = true;
                        break;
                    }
                }
                if (component.hasDynamicView) break;
            }
        }

        if (component.components && Array.isArray(component.components)) {
            for (const child of component.components) {
                this.populateDynamicHandlerFlags(child, config, appSite);
            }
        }
    },

    getResourceStatistics(this: LoaderPreProcessJson, appSite: string): ResourceStatistics {
        if (this._resourceStatsCache.has(appSite)) {
            return this._resourceStatsCache.get(appSite)!;
        }
        const stats = this.computeResourceStatistics(appSite);
        this._resourceStatsCache.set(appSite, stats);
        return stats;
    },

    computeResourceStatistics(this: LoaderPreProcessJson, appSite: string): ResourceStatistics {
        const stats = new ResourceStatistics();

        for (const [key, value] of this._htmlJsonCache.entries()) {
            if (key.toLowerCase().startsWith(appSite.toLowerCase() + '_') && value) {
                stats.templateCount++;
            }
        }

        for (const [key, value] of this._dataJsonCache.entries()) {
            if (key.toLowerCase().startsWith(appSite.toLowerCase() + '_') && value) {
                stats.jsonFileCount++;
                (this.constructor as typeof LoaderPreProcessJson & { countJsonKeysAndArrays: typeof accessorStaticMethods.countJsonKeysAndArrays })
                    .countJsonKeysAndArrays(value as unknown as PlainJsonObject, stats);
            }
        }

        const dynamicDataConfig = this.getDynamicDataConfig(appSite);
        if (dynamicDataConfig) {
            stats.dynamicDataHandlerCount = (this.constructor as typeof LoaderPreProcessJson & { countHandlersInConfig: typeof accessorStaticMethods.countHandlersInConfig })
                .countHandlersInConfig(dynamicDataConfig);
        }

        const dynamicViewConfig = this.getDynamicViewConfig(appSite);
        if (dynamicViewConfig) {
            stats.dynamicViewHandlerCount = (this.constructor as typeof LoaderPreProcessJson & { countHandlersInConfig: typeof accessorStaticMethods.countHandlersInConfig })
                .countHandlersInConfig(dynamicViewConfig);
        }

        return stats;
    },

    shouldSkipHandler(this: LoaderPreProcessJson, handlerType: string, skipPatterns: string | Set<string> | null): boolean {
        if (!skipPatterns) {
            return false;
        }

        if (typeof skipPatterns === 'string') {
            if (skipPatterns === '*') {
                return true;
            }

            const handlerLower = handlerType.toLowerCase();
            for (const rawPattern of skipPatterns.split(',')) {
                const pattern = rawPattern.trim();
                if (pattern && handlerLower.indexOf(pattern.toLowerCase()) >= 0) {
                    return true;
                }
            }
            return false;
        }

        if (skipPatterns.size === 0) {
            return false;
        }

        const handlerTypeLower = handlerType.toLowerCase();
        for (const pattern of skipPatterns) {
            if (pattern === '*' || handlerTypeLower === pattern.toLowerCase()) {
                return true;
            }
        }
        return false;
    },

    getLoaderStateJson(this: LoaderPreProcessJson, appSite: string): string {
        const sb: string[] = [];
        sb.push('{');

        sb.push('"TemplateKeys":[');
        const allKeys = new Set<string>();
        if (this._htmlJsonCache.has(appSite)) {
            for (const k of this._htmlJsonCache.get(appSite)!.keys()) allKeys.add(k);
        }
        if (this._dataJsonCache.has(appSite)) {
            for (const k of this._dataJsonCache.get(appSite)!.keys()) allKeys.add(k);
        }
        const sortedKeys = Array.from(allKeys).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < sortedKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(sortedKeys[i]); sb.push('"');
        }
        sb.push('],');

        sb.push('"GlobalDataStore":{');
        const gdKeys = Object.keys(this._globalDataStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < gdKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(gdKeys[i]); sb.push('":');
            const gd = this._globalDataStore[gdKeys[i]];
            sb.push(gd ? JsonUtility.serializeObject(gd) : 'null');
        }
        sb.push('},');

        sb.push('"GlobalViewDataStore":{');
        const gvdKeys = Object.keys(this._globalViewDataStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < gvdKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(gvdKeys[i]); sb.push('":');
            const gvd = this._globalViewDataStore[gvdKeys[i]];
            sb.push(gvd ? JsonUtility.serializeObject(gvd) : 'null');
        }
        sb.push('},');

        sb.push('"GlobalViewDataComponentStore":{');
        const gvdcKeys = Object.keys(this._globalViewDataComponentStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < gvdcKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(gvdcKeys[i]); sb.push('":');
            const gvdc = this._globalViewDataComponentStore[gvdcKeys[i]];
            sb.push(gvdc ? JsonUtility.serializeObject(gvdc) : 'null');
        }
        sb.push('},');

        sb.push('"DynamicDataConfigStore":{');
        const ddKeys = Object.keys(this._dynamicDataConfigStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < ddKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(ddKeys[i]); sb.push('":');
            const dd = this._dynamicDataConfigStore[ddKeys[i]];
            sb.push(dd ? dd.size.toString() : 'null');
        }
        sb.push('},');

        sb.push('"DynamicViewConfigStore":{');
        const dvKeys = Object.keys(this._dynamicViewConfigStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < dvKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(dvKeys[i]); sb.push('":');
            const dv = this._dynamicViewConfigStore[dvKeys[i]];
            sb.push(dv ? dv.size.toString() : 'null');
        }
        sb.push('},');

        sb.push('"DynamicActionConfigStore":{');
        const daKeys = Object.keys(this._dynamicActionConfigStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < daKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(daKeys[i]); sb.push('":');
            const da = this._dynamicActionConfigStore[daKeys[i]];
            sb.push(da ? da.size.toString() : 'null');
        }
        sb.push('},');

        sb.push('"DynamicDataParamsKeys":[');
        const ddpKeys = Array.from(this._dynamicDataParamsStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < ddpKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(ddpKeys[i]); sb.push('"');
        }
        sb.push('],');

        sb.push('"DynamicViewParamsKeys":[');
        const dvpKeys = Array.from(this._dynamicViewParamsStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < dvpKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(dvpKeys[i]); sb.push('"');
        }
        sb.push('],');

        sb.push('"DynamicActionParamsKeys":[');
        const dapKeys = Array.from(this._dynamicActionParamsStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < dapKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(dapKeys[i]); sb.push('"');
        }
        sb.push(']');

        sb.push('}');
        return sb.join('');
    },

    getLoaderPerSiteStoresJson(this: LoaderPreProcessJson, appSite: string): string {
        const sb: string[] = [];
        this._serializePerSiteStores(sb, appSite);
        const json = sb.join('');
        const prefix = ',"PerSiteStores":';
        return json.startsWith(prefix) ? json.substring(prefix.length) : '{}';
    }
};

export const accessorStaticMethods = {
    buildAppSitesToLoad(appSite: string, searchAppSites: string): string[] {
        const appSitesToLoad = [appSite];
        if (searchAppSites) {
            const searchSites = searchAppSites.split(',');
            for (const site of searchSites) {
                const trimmed = site.trim();
                if (trimmed && !appSitesToLoad.includes(trimmed)) {
                    appSitesToLoad.push(trimmed);
                }
            }
        }
        return appSitesToLoad;
    },

    loadTemplatesFromSingleAppSite(
        config: IConfig,
        appSiteName: string,
        sourceHtmlCache: Map<string, Map<string, SourceFile>>,
        sourceJsonCache: Map<string, Map<string, TargetFile>>
    ): void {
        const contentNames = config.getAllContentNames(appSiteName);
        if (contentNames.length === 0) {
            Logger.warn(`No content names found for AppSite: ${appSiteName}`, 'LoaderPreProcessJson');
            return;
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`Loading templates for AppSite: ${appSiteName} (${contentNames.length} files)`, 'LoaderPreProcessJson');
        }

        sourceHtmlCache.set(appSiteName, new Map());
        sourceJsonCache.set(appSiteName, new Map());

        // Non-Normal engine repos drop raw *.html; the template's structure ships as a pre-built HtmlJson
        // artifact instead. Register those template names too so the Load() HtmlJson pass rehydrates them
        // from the artifact (empty raw content) rather than skipping them for lack of a .html file.
        const htmlJsonArtifactNames = new Set<string>();
        for (const n of config.getArtifactNames(appSiteName, ArtifactKind.HtmlJson)) htmlJsonArtifactNames.add(n.toLowerCase());

        for (const contentName of contentNames) {
            if (CommonUtil.isDynamicOrGlobalConfigName(contentName)) continue;

            const relativePath = config.getRelativeInfo(appSiteName, contentName) || '';

            const htmlContent = config.getHtmlContent(appSiteName, contentName);
            if (htmlContent != null) {
                const sourceFile = new SourceFile();
                sourceFile.relativePath = relativePath;
                sourceFile.content = htmlContent;
                sourceHtmlCache.get(appSiteName)!.set(contentName, sourceFile);
            } else if (htmlJsonArtifactNames.has(contentName.toLowerCase())) {
                // Raw *.html excluded but the pre-built HtmlJson artifact carries this template — register
                // by name with empty content so Load() rehydrates the structure from the artifact.
                const sourceFile = new SourceFile();
                sourceFile.relativePath = relativePath;
                sourceFile.content = '';
                sourceHtmlCache.get(appSiteName)!.set(contentName, sourceFile);
            }

            const jsonData = config.getDataJson(appSiteName, contentName);
            if (jsonData) {
                const targetFile = new TargetFile();
                targetFile.relativePath = relativePath;
                targetFile.jsonStructure = jsonData;
                sourceJsonCache.get(appSiteName)!.set(contentName, targetFile);
            }
        }
    },

    getReadOnlySourceJsonCache(
        sourceJsonCache: Map<string, Map<string, TargetFile>>
    ): Map<string, ReadonlyMap<string, TargetFile>> {
        const result = new Map<string, ReadonlyMap<string, TargetFile>>();
        for (const [appSite, templates] of sourceJsonCache.entries()) {
            result.set(appSite, templates);
        }
        return result;
    },

    countJsonKeysAndArrays(json: PlainJsonObject | JsonObject, stats: ResourceStatistics): void {
        if (!json) return;

        const keys: Iterable<string> = json instanceof JsonObject ? json.keys() : Object.keys(json);

        for (const key of keys) {
            stats.jsonKeyCount++;

            let value: unknown = json instanceof JsonObject ? json.get(key) : (json as PlainJsonObject)[key];

            if (value instanceof JsonValue) {
                if (value.isObject) {
                    value = value.asObject();
                } else if (value.isArray) {
                    value = value.asArray();
                } else {
                    continue;
                }
            }

            if (value instanceof JsonObject) {
                accessorStaticMethods.countJsonKeysAndArrays(value, stats);
            } else if (value instanceof JsonArray) {
                stats.jsonArrayCount++;
                for (let i = 0; i < value.length; i++) {
                    let item: unknown = value.get(i);
                    if (item instanceof JsonValue) {
                        if (item.isObject) {
                            item = item.asObject();
                        } else {
                            continue;
                        }
                    }
                    if (item instanceof JsonObject) {
                        accessorStaticMethods.countJsonKeysAndArrays(item, stats);
                    }
                }
            } else if (Array.isArray(value)) {
                stats.jsonArrayCount++;
                for (const item of value) {
                    if (item && typeof item === 'object' && !(item instanceof JsonArray)) {
                        accessorStaticMethods.countJsonKeysAndArrays(item as PlainJsonObject, stats);
                    }
                }
            } else if (value && typeof value === 'object' && !(value instanceof JsonArray)) {
                accessorStaticMethods.countJsonKeysAndArrays(value as PlainJsonObject, stats);
            }
        }
    },

    countHandlersInConfig(config: DynamicConfigMap | PlainJsonObject): number {
        let count = 0;
        const keys: Iterable<string> = config instanceof JsonObject ? config.keys() : Object.keys(config);

        for (const componentKey of keys) {
            let handlersArray: unknown = config instanceof JsonObject ? config.get(componentKey) : (config as PlainJsonObject)[componentKey];

            if (handlersArray instanceof JsonValue) {
                if (handlersArray.isArray) {
                    handlersArray = handlersArray.asArray();
                } else {
                    continue;
                }
            }

            if (handlersArray) {
                if (handlersArray instanceof JsonArray) {
                    count += handlersArray.length;
                } else if (Array.isArray(handlersArray)) {
                    count += handlersArray.length;
                }
            }
        }
        return count;
    },

    dumpHtmlJsonDebug(sb: string[], dict: JsonObject, indent: number): void {
        const prefix = ' '.repeat(indent * 2);
        for (const [key, value] of dict.entries()) {
            sb.push(prefix);
            sb.push(key);
            sb.push(': ');
            if (value.isObject) {
                sb.push('{');
                sb.push('\n');
                accessorStaticMethods.dumpHtmlJsonDebug(sb, value.getObject(), indent + 1);
                sb.push(prefix);
                sb.push('}');
            } else {
                sb.push(value.toString());
            }
            sb.push('\n');
        }
    }
};
